import { useState } from "react";
import { Phone, Mail, MapPin, Loader2, Send, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Reveal } from "@/lib/motion";
import { COMPANY, PRODUCTS } from "@/data/content";
import { submitLead } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", machinery: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, email: form.email || null, source: "contact" });
      toast.success("Thank you! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", machinery: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${COMPANY.whatsapp}`;

  return (
    <section id="contact" data-testid="contact" className="bg-brand-ink py-24 lg:py-32 relative overflow-hidden">
      <div className="grain" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-greenLight mb-4">Contact Us</p>
          <h2 className="font-heading font-bold text-brand-cream tracking-tight text-3xl sm:text-5xl max-w-2xl leading-[1.05]">
            {"Let's get the right machinery to your field."}
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="space-y-5">
                {[
                  { Icon: MapPin, label: "Office", value: COMPANY.address },
                  { Icon: Phone, label: "Call", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
                  { Icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                ].map(({ Icon, label, value, href }) => (
                  <div key={label} className="flex gap-4">
                    <span className="grid place-items-center size-11 rounded-lg bg-brand-cream/10 text-brand-greenLight shrink-0">
                      <Icon className="size-5" />
                    </span>
                    <div>
                      <div className="font-mono text-xs uppercase tracking-[0.15em] text-brand-cream/50">{label}</div>
                      {href ? (
                        <a href={href} className="text-brand-cream hover:text-brand-greenLight transition-colors">{value}</a>
                      ) : (
                        <p className="text-brand-cream/85 leading-relaxed">{value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-3">
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  <MessageCircle className="size-4" /> WhatsApp Chat
                </a>
                <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-call" className="inline-flex items-center gap-2 rounded-full bg-brand-blue text-white px-5 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  <Phone className="size-4" /> Call Now
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl border border-brand-cream/15">
                <iframe
                  title="ADK SOLUTIONS location"
                  src={COMPANY.mapEmbed}
                  className="w-full h-56 grayscale-[0.2]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl bg-brand-cream p-7 sm:p-9 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-name">Name *</Label>
                    <Input id="c-name" data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your full name" className="h-11 bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-phone">Phone Number *</Label>
                    <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" className="h-11 bg-white" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-email">Email Address</Label>
                    <Input id="c-email" data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="h-11 bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Machinery Interested In</Label>
                    <Select value={form.machinery} onValueChange={(v) => setForm((f) => ({ ...f, machinery: v }))}>
                      <SelectTrigger data-testid="contact-machinery" className="h-11 bg-white"><SelectValue placeholder="Select a product" /></SelectTrigger>
                      <SelectContent>
                        {PRODUCTS.map((p) => <SelectItem key={p.slug} value={p.name}>{p.name}</SelectItem>)}
                        <SelectItem value="Other / Not sure">Other / Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-msg">Message</Label>
                  <Textarea id="c-msg" data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your farm and requirements…" rows={4} className="bg-white" />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit" className="rounded-full bg-brand-green text-white hover:bg-brand-greenDark h-12 px-8">
                  {loading ? <Loader2 className="animate-spin" /> : <Send className="size-4" />}
                  {loading ? "Sending…" : "Send Message"}
                </Button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
